export const dipiPalette = {
	primary: {
		light: '#9dd4f7',
		lighter: '#e4f2fc'
	},
	disabled: {
		light: '#c0c0c0',
		darker: '#403e3e'
	},
	warning: {
		light: '#fcc937',
		lighter: '#fdde82'
	},
	success: {
		light: '#5db975',
		lighter: '#85cb98',
		darker: '#097314',
	},
	error: {
		light: '#ee695d',
		lighter: '#f5a19a'
	},
	notice: {
		light: '#A997DF',
		lighter: '#e29fed',
		main: '#ba68c8',
	}
}


/** The brand colours the announcement surfaces are built from.
 *
 * They are literals here, and they have to be: `AnnouncementFrame` builds a stylesheet as a
 * module-level template string for a sandboxed iframe, which is outside React and cannot call
 * `useTheme()`. The values are DIP Insights' own — `AnalyticsThemeProvider`'s `primary`
 * (`@ui-seeker/app`) with the `light`/`lighter` this file already overrides above.
 *
 * **They match the application's own primary button, on purpose** (measured off
 * `New Announcement`, 2026-09-29): flat `#6aa8dc`, hover `#4d57a8`, shadow tinted with the
 * same blue. Two earlier attempts used the dark end of the ramp — `primary.darker` is a
 * purple, and the header navy a near-black — and both read as a surface borrowed from
 * somewhere else. Matching the button is what makes an announcement look like this product.
 *
 * ⚠️ White on `#6aa8dc` is ~2.6:1. That is the application's existing standard for its primary
 * button, not something introduced here; changing it is a decision about the whole product.
 */
const brand = {
	primaryMain:    '#6aa8dc',  // the app's primary button
	primaryDark:    '#4d57a8',  // its hover
	secondaryMain:  '#006257',
	/** The two ends of the surface ramp, both `primaryMain` darkened — MUI's `darken()`, i.e.
	 *  every channel at 98% and at 84%. Their midpoint is `#629bca`, the flat blue that was
	 *  approved on screen (2026-09-29), so the gradient reads as that same colour with a
	 *  gentle fall rather than as a new one.
	 *
	 *  Deliberately NOT ending on `primaryDark`: that one jumps to violet, which is where two
	 *  earlier attempts went wrong. The whole ramp stays inside the app's blue. */
	surfaceFrom:    '#68a5d8',  // darken(main, 0.02)
	surfaceTo:      '#598db9',  // darken(main, 0.16)
}

const SURFACE_RAMP = `linear-gradient(135deg, ${brand.surfaceFrom} 0%, ${brand.surfaceTo} 100%)`

export const announcementColors = {
	/** Dialog header, bell panel header and the unread capsule. */
	brandSurface: SURFACE_RAMP,
	/** The call to action inside a body, and the primary button of the dialogs. */
	cta: SURFACE_RAMP,
	/** Flat on hover, and that is the application's own button hover — the ramp is what the
	 *  resting state says, the flat dark is the press. */
	ctaHover: brand.primaryDark,
	/** Two layers: a tight neutral one for the edge, a wide one tinted with the brand blue —
	 *  the same tint the application's own buttons cast. */
	surfaceShadow: '0 2px 4px rgba(0,0,0,0.04), 0 12px 40px rgba(106,168,220,0.22)',
	/** Links inside an announcement body — on white, so the blue is fine here. */
	link: brand.primaryMain,
	linkHover: brand.primaryDark,
	/** Neutrals for the body stylesheet. Not brand, just ink and rules. */
	ink: '#1F2937',
	inkStrong: '#111827',
	inkMuted: '#6B7280',
	border: '#E0E3E7',
	surfaceMuted: '#F4F6F8',
}